import React, { useEffect } from 'react';
import { Form } from 'antd';

import { setStaticFormValuesFromFilter } from '../../common/filterHelpers';
import { SearchFormFieldEnum, SearchFormProps } from '../../common/searchFormField.types';
import { PriorityEnum } from '../../common/types';
import { useGetAllClients } from '../../common/useGetAllClients';
import { useGetAllUsers } from '../../common/useGetAllUsers';
import { useGetCitiesDictionary } from '../../common/useGetCitiesDictionary';
import { FormCheckbox } from '../../components/forms/FormCheckbox';
import { FormCheckboxGroup } from '../../components/forms/FormCheckboxGroup';
import { FormDatePicker } from '../../components/forms/FormDatePicker';
import { FormDoubleInput } from '../../components/forms/FormDoubleInput';
import { FormInput } from '../../components/forms/FormInput';
import { FormProjectSelectWithSearch } from '../../components/forms/FormProjectSelectWithSearch';
import { FormSelect } from '../../components/forms/FormSelect';
import { FormSelectMultiple } from '../../components/forms/FormSelectMultiple';
import { StyledColumn } from '../../components/StyledColumn';

export const ProjectsSearchForm: React.FC<SearchFormProps> = ({ form, filter }) => {
  useEffect(() => setStaticFormValuesFromFilter(form, filter), [filter, form]);

  const { data: usersData, isLoading: usersLoading } = useGetAllUsers();
  const { data: clientsData, isLoading: clientsLoading } = useGetAllClients();

  const usersOptions =
    !usersLoading && usersData
      ? usersData.results.map((user) => ({
          value: user.id,
          content: `${user.firstName} ${user.lastName}`,
        }))
      : [];

  const clientsOptions =
    !clientsLoading && clientsData
      ? clientsData.results.map((client) => ({
          value: client.name,
          content: client.name,
        }))
      : [];

  const { data, isLoading } = useGetCitiesDictionary();
  const citiesOptions = data && !isLoading ? data?.map((city) => ({ value: city.value, content: city.value })) : [];

  const priorityOptions = Object.values(PriorityEnum);

  return (
    <Form form={form} style={{ flexGrow: '1' }} layout="vertical" colon={false}>
      <StyledColumn gap="0.5rem">
        <FormProjectSelectWithSearch />

        <FormInput name={SearchFormFieldEnum.ExternalId} label="External ID" />

        <FormSelect name="client.name" label="Project client name" options={clientsOptions} />

        <FormDoubleInput
          leftInput={
            <FormInput name={SearchFormFieldEnum.ClientEmployeeFirstName} label="Client employee first name" />
          }
          rightInput={<FormInput name={SearchFormFieldEnum.ClientEmployeeLastName} label="Client employee last name" />}
        />

        <FormDoubleInput
          leftInput={<FormDatePicker name={['createdDate', 'start']} label="Created from" />}
          rightInput={<FormDatePicker name={['createdDate', 'end']} label="Created to" />}
          withDivider
        />

        <FormCheckboxGroup options={priorityOptions} label="Priority" name="priority" />

        <FormCheckbox label="Active" name="active" />

        <FormSelectMultiple name="city" label="Cities" options={citiesOptions} />

        <FormSelect name="coordinators.id" label="Project coordinator" options={usersOptions} />

        <FormSelect name="backoffices.id" label="Project backoffice" options={usersOptions} />

        <FormSelect name="recruiters.id" label="Project recruiter" options={usersOptions} />

        <FormSelect name="interviewers.id" label="Project interviewer" options={usersOptions} />
      </StyledColumn>
    </Form>
  );
};
